import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, ViewChild } from '@angular/core';
import { firstValueFrom, Observable, Subject, take } from 'rxjs';
import { debounceTime, switchMap, filter } from 'rxjs/operators';
import { Catalog } from 'src/app/shared/models/echo/catalog';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { GeographyApiService } from 'src/app/shared/services/echo/geography.api.service';
import { UserLogicService } from 'src/app/shared/services/user.logic.service';
import { SetupLogicService } from 'src/app/shared/services/setup.logic.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MetaboliteApiService } from 'src/app/shared/services/echo/metabolite.api.service';
import { Metabolite } from 'src/app/shared/models/echo/metabolite';
import { ModelLibraryCollection } from 'src/app/shared/models/model-library';
import { Constants } from 'src/app/shared/utils/constants';
import { TabMenuLogicService } from 'src/app/shared/services/tab-menu.logic.service';
import { CustomTabMenuItem } from 'src/app/shared/models/custom-tab-menu-item';
import { ModelToBeRun, ModelToBeRunRow } from 'src/app/shared/models/run/model-to-be-run';
import { Utils } from 'src/app/shared/utils/utils';
import { EFSetupModelsToBeRunComponent } from '../ef-setup-models-to-be-run/ef-setup-models-to-be-run.component';
import { ActiveIngredientApiService } from 'src/app/shared/services/echo/active-ingredient.api.service';
import { Molecule } from 'src/app/shared/models/echo/molecule';
import { EfateSetupApiService } from 'src/app/shared/services/echo/efate-setup.api.service';
import { SaveStructure, TransactionType } from 'src/app/shared/models/transaction-type';
import { EfateSetup } from 'src/app/shared/models/echo/efate-setup';

@Component({
  selector: 'app-ef-setup',
  templateUrl: './ef-setup.component.html',
  styleUrls: ['./ef-setup.component.css']
})
export class EFSetupComponent implements OnInit {

  @ViewChild(EFSetupModelsToBeRunComponent) modelsToBeRunComponent!: EFSetupModelsToBeRunComponent;

  @Input() menuService!: TabMenuLogicService;
  @Input() activeItem?: CustomTabMenuItem;
  @Input() columnDef: any[] = [];
  @Input() disciplinePk: number = 0;
  @Input() modelsCollection: ModelLibraryCollection[] = [];
  
  @Output() isSetupValidEvent = new EventEmitter<boolean>();
  @Output() modelsSelectedEvent = new EventEmitter<string[]>();

  geographies: Catalog[] = [];
  modelsToBeRun: ModelToBeRun[] = [];
  currentUserName: string = "";
  modelsLoading: boolean = false;
  setupLoading: boolean = false;
  validSetup: boolean = false;
  destroyRef = inject(DestroyRef)
  activeIngredients: Molecule[] = [];
  allActiveIngredients: Molecule[] = [];
  moleculeSelected: Array<{ moleculePk: number, activeIngredientName: string, concentration: string, unit: string, mainActive: boolean, aeConcentration: string }> = [];
  
  selectedActiveIngredient: Molecule | undefined;
  selectedGeography: Catalog | undefined;

  private activeIngredientChange$ = new Subject<Molecule>();
  private readonly allowedGeographies = [
    Constants.CROP_GEOGRAPHIES.EUROPE_UNION,
    Constants.CROP_GEOGRAPHIES.USA,
  ];

  constructor(
    private geograpthyAPIService: GeographyApiService,
    private orderByPipe: OrderByPipe,
    private userLogicService: UserLogicService,
    private metaboliteApiService: MetaboliteApiService,
    private activeIngredientApiService: ActiveIngredientApiService,
    private setupLogicService: SetupLogicService,
    private efateSetupApiService: EfateSetupApiService,
  ) { }

  ngOnInit(): void {
    this.setupLoading = true;
    this.getGeographies();
    this.getAllActives();
    this.getCreatedBy();
    this.initSubscribes();
    this.setupFormulationChangeHandler();
  }

  getGeographies() {
    this.geograpthyAPIService.getRegionCountryCatalog().pipe(take(1)).subscribe(geographies => {
      this.geographies = this.orderByPipe.transform(
        geographies.filter((geography) => !!geography.name && this.allowedGeographies.includes(geography.name)),
        'name'
      );
    });
  }
  
  getAllActives() {
    this.activeIngredientApiService.getAllActiveIngredients().pipe(take(1)).subscribe(actives => {
      this.allActiveIngredients = this.orderByPipe.transform(actives, 'moleculeName');
      this.activeIngredients = [];
    });
  }

  onActiveIngredientFilterChanged(query: string) {
    if (!query) {
      this.activeIngredients = this.allActiveIngredients.slice(0, 50);
      return;
    }
    const lowerQuery = query.toLowerCase();
    this.activeIngredients = this.allActiveIngredients.filter(
      active => active.moleculeName?.toLowerCase().includes(lowerQuery)
    );
  }

  onActiveIngredientSelectionChanged(activeIngredient: Molecule | undefined): void {
    this.selectedActiveIngredient = activeIngredient;
    if (!activeIngredient) {
      this.modelsToBeRun = [];
      return;
    }
    this.loadMetabolitesIfReady();
  }

  onGeographySelectionChanged(geography: Catalog | undefined): void {
    this.selectedGeography = geography;
    this.setupLogicService.selectedGeographyKey.set(geography);
    if (!geography) {
      this.modelsToBeRun = [];
      return;
    }
    this.loadMetabolitesIfReady();
  }

  private loadMetabolitesIfReady(): void {
    if (this.selectedActiveIngredient && this.selectedGeography) {
      this.activeIngredientChange$.next(this.selectedActiveIngredient);
    }
  }

  onSetupValidChanged(isValid: boolean): void {
    this.validSetup = isValid;
    this.isSetupValidEvent.emit(this.validSetup);
  }

  initSubscribes() {
    this.menuService.activeIndexChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (changed: boolean) => {
      if (changed) {
        await this.handleTabChange();
      }
    });
  }

  getCreatedBy() {
    this.currentUserName = this.userLogicService.profile.displayName ?? "";
  }

  private setupFormulationChangeHandler(): void {
    this.activeIngredientChange$.pipe(
      filter(activeIngredient => !!activeIngredient),
      debounceTime(500),
      switchMap(activeIngredient => this.processActiveIngredientChangeObservable(activeIngredient)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => { },
      error: (error) => { }
    });
  }

  private processActiveIngredientChangeObservable(activeIngredient: Molecule) {
    return new Observable<void>(observer => {
      this.processActiveIngredientChange(activeIngredient).then(() => {
        observer.next();
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  private async processActiveIngredientChange(activeIngredient: Molecule): Promise<void> {
    if (!activeIngredient) {
      this.modelsToBeRun = [];
      this.modelsLoading = false;
      return;
    }

    const requestedMoleculePk = activeIngredient.moleculePk;
    const requestedGeography = this.selectedGeography?.name?.trim();
    if (!requestedMoleculePk || !requestedGeography) {
      this.modelsToBeRun = [];
      this.modelsLoading = false;
      return;
    }

    this.modelsLoading = true;
    let localModelList: ModelToBeRun[] = [];
    const selectedMetabolitePks = await this.getSavedMetabolitePks(requestedMoleculePk, requestedGeography);

    await Promise.all([activeIngredient].map(async (active: Molecule) => {
      let rowData: ModelToBeRunRow[] = [];
      let selected = false;

      await this.getMetabolitesByMolecule(active.moleculePk!).then((metabolites: Metabolite[]) => {
        rowData = metabolites.map((metabolite) => {
            let row: ModelToBeRunRow = { ...metabolite };
            row.moleculePk = active.moleculePk;
            this.setRowCollectionForModel(row);
            if (selectedMetabolitePks.has(row.metabolitePk ?? -1)) {
              this.applyDefaultModelsToRow(row);
              selected = true;
            }
            return row;
          });

        rowData = this.orderByPipe.transform(rowData, 'metaboliteName');
      });

      rowData = Utils.orderDataByCompoundOrder(rowData);

      localModelList.push({
        activeIngredient: active,
        rowData,
        selected: selected,
      });
    }));

    const currentMoleculePk = this.selectedActiveIngredient?.moleculePk;
    const currentGeography = this.selectedGeography?.name?.trim();
    if (currentMoleculePk !== requestedMoleculePk || currentGeography !== requestedGeography) {
      this.modelsLoading = false;
      return;
    }

    this.modelsToBeRun = localModelList;
    this.modelsLoading = false;
  }

  private async getSavedMetabolitePks(moleculePk?: number, geography?: string): Promise<Set<number>> {
    if (!moleculePk || !geography) {
      return new Set<number>();
    }

    try {
      const savedSetup = await firstValueFrom(this.efateSetupApiService.getEfateSetupByMoleculeAndGeography(moleculePk, geography).pipe(take(1)));
      const metabolitePks = savedSetup?.efateMetaboliteXMolecules?.map(x => x.metabolitePk) ?? [];
      return new Set<number>(metabolitePks);
    } catch {
      return new Set<number>();
    }
  }

  private applyDefaultModelsToRow(row: ModelToBeRunRow): void {
    const selectedGeography = this.selectedGeography;

    this.modelsCollection.forEach((collection: ModelLibraryCollection) => {
      const availableModels = selectedGeography
        ? collection.library?.filter(model =>
            model.geographyXModel?.some(geography => this.setupLogicService.isSelectedGeography(geography, selectedGeography))
          )
        : collection.library;

      const defaultModel = availableModels?.[0];
      if (defaultModel) {
        (row as any)[collection.name] = [defaultModel];
      }
    });
  }

  onSelectedMoleculesChanged(selectedMolecules: Array<{ moleculePk: number, activeIngredientName: string, concentration: string, unit: string, mainActive: boolean, aeConcentration: string }>): void {
    this.moleculeSelected = selectedMolecules;
  }

  private getCheckedMoleculePks(): number[] {
    if (!this.modelsToBeRunComponent) return [];
    const selectedMolecules = this.modelsToBeRunComponent.getSelectedMolecules();
    const checkedMoleculePks = selectedMolecules.map(m => m.moleculePk);
    return checkedMoleculePks;
  }

  private async handleTabChange(): Promise<void> {
    const saved = await this.saveEfateSetup();
    if (!saved) {
      this.menuService.setSuccess(false);
      return;
    }

    const currentProjectMoleculePks = this.getCheckedMoleculePks();
    if (currentProjectMoleculePks.length > 0) {
      this.modelsSelectedEvent.emit(currentProjectMoleculePks.map(pk => pk.toString()));
    }

    this.menuService.setSuccess(true);
  }

  private async saveEfateSetup(): Promise<boolean> {
    const efateSetup = this.buildEfateSetupToSave();
    if (!efateSetup) {
      return true;
    }

    const payload: SaveStructure[] = [{
      transactionType: TransactionType.update,
      row: efateSetup,
    }];

    try {
      await firstValueFrom(this.efateSetupApiService.saveEfateSetup(payload).pipe(take(1)));
      return true;
    } catch (error) {
      console.warn('Error saving EFATE setup', error);
      return false;
    }
  }

  private buildEfateSetupToSave(): EfateSetup | undefined {
    const moleculePk = this.selectedActiveIngredient?.moleculePk;
    const geography = this.selectedGeography?.name?.trim();

    if (!moleculePk || !geography) {
      return undefined;
    }

    const selectedMetabolitePks = this.getSelectedMetabolitePks();

    return {
      moleculePk,
      geography,
      createdBy: this.currentUserName,
      ...(selectedMetabolitePks.length > 0
        ? { efateMetaboliteXMolecules: selectedMetabolitePks.map(metabolitePk => ({ metabolitePk })) }
        : {}),
    };
  }

  private getSelectedMetabolitePks(): number[] {
    const metabolitePks = new Set<number>();

    this.modelsToBeRun.forEach(model => {
      model.rowData.forEach(row => {
        const hasModelSelection = this.modelsCollection.some(collection => {
          const values = (row as any)[collection.name] as unknown;
          return Array.isArray(values) && values.length > 0;
        });

        if (hasModelSelection && row.metabolitePk) {
          metabolitePks.add(row.metabolitePk);
        }
      });
    });

    return Array.from(metabolitePks);
  }

  orderedModelsToBeRunRowData() {
    this.modelsToBeRun.forEach(model => {
      model.rowData = Utils.orderDataByCompoundOrder(model.rowData);
    });
  }

  setRowCollectionForModel(row: ModelToBeRunRow, useFilter: boolean = false) {
    this.modelsCollection.forEach((collection: ModelLibraryCollection) => {
      switch (collection.name) {
        case Constants.COMPARTMENTS.CAKE.toLowerCase():
          break;
      }
    });
  }

  getMetabolitesByMolecule(moleculePk: number) {
    return firstValueFrom(this.metaboliteApiService.getMetabolitesByMolecule(moleculePk));
  }

}
